<template lang="pug">
    el-card.contains-table.currencies(shadow="always")
        .error(v-if="error")
            template(v-if="error.code")
                Error(:message="$t(error.message || 'Something went wrong!')" :code="error.code")
            template(v-else)
                Error(:message="$t('Something went wrong!')" :code="501")
        .currencies-table(v-else)
            paginated-items(
                scope="ex"
                type="shop_currency"
                :loading="loading"
                :items="currenciesPaginated.items"
                :filters="filters"
                :show-filters="showFilters"
                :variables="currenciesPaginated.variables"
                :all-columns="currenciesPaginated.allColumns"
                :active-columns="currenciesPaginated.activeColumns"
                :page-info="currenciesPaginated.pageInfo"
                :sort="sort"
                @pagination-size-change="handleSizeChange"
                @pagination-current-change="handleCurrentChange"
                @show-table-columns="handleColsShow"
                @change-filter="handleFilterInputChange"
                @click="openModal"
                @change-sort="handleSortInputChange"
            )
                template(slot="top")
                    el-row.table-top(v-loading="loading" type="flex" justify="space-between")
                        .row
                            .left
                                el-button(
                                    v-if="!sharedShopDataReadOnly"
                                    type="primary"
                                    size="mini"
                                    icon="el-icon-fa-plus fas"
                                    @click="openModal()"
                                )
                                    | {{ $t('New Currency') }}
                            .right.hide-on-empty
                                el-button(
                                    plain
                                    size="mini"
                                    type="primary"
                                    icon="el-icon-fa-filter fas"
                                    @click="toggleFilterVisibility"
                                    :disabled="disable.showFilters || hasFilters"
                                )
                                    span(v-if="showFilters") {{ $t('Hide filters') }}
                                    span(v-else) {{ $t('Show filters') }}
                template(v-slot:actions="{ item }")
                    el-button.delete-button(
                        v-if="!sharedShopDataReadOnly"
                        plain
                        circle
                        size="mini"
                        type="danger"
                        icon="el-icon-fa-trash-alt fas"
                        @click.stop.prevent="openDeleteDialog(item)"
                    )

        currency-form(
            :data="modal.data"
            :visibility.sync="modal.visibility"
            :saving="modal.saving"
            :existing-iso-codes="existingIsoCodes"
            @submit="saveCurrency"
            @delete="(id) => openDeleteDialog({ id })"
            @closed="resetModal"
        )

        el-dialog(
            :title="$t('Delete Currency')"
            :visible.sync="deleteDialog.visibility"
            :close-on-click-modal="false"
            top="10vh"
            width="460px"
        )
            .body(v-loading="deleteDialog.loading")
                p {{ $t('Do you really want to delete the currency?') }}
            template(slot="footer")
                .modal-footer
                    .right
                        el-button(
                            size="mini"
                            icon="el-icon-fa-times fas"
                            :disabled="deleteDialog.loading"
                            @click="closeDeleteDialog"
                        ) {{ $t('Cancel') }}
                        el-button(
                            size="mini"
                            type="danger"
                            icon="el-icon-fa-trash-alt fas"
                            :disabled="deleteDialog.loading"
                            @click="confirmDelete"
                        ) {{ $t('Delete') }}
</template>

<i18n>
{
    "de": {
        "New Currency": "Neue Währung",
        "Show filters": "Filter anzeigen",
        "Hide filters": "Filter verbergen",
        "Delete Currency": "Währung löschen",
        "Do you really want to delete the currency?": "Möchten Sie die Währung wirklich löschen?",
        "Cancel": "Abbrechen",
        "Delete": "Löschen",
        "The currency has been saved successfully": "Die Währung wurde erfolgreich gespeichert",
        "The currency could not be saved": "Die Währung konnte nicht gespeichert werden",
        "The currency has been deleted successfully": "Die Währung wurde erfolgreich gelöscht",
        "The currency could not be deleted": "Die Währung konnte nicht gelöscht werden",
        "Currency list could not be fetched": "Die Währungsliste konnte nicht abgerufen werden"
    }
}
</i18n>

<script>
import _ from 'lodash';
import pinia from '@/pinia';
import { useSetupStore, useShopCurrencyStore } from '@/stores';
import { adminClient as apolloClient } from '@/store/apollo';

import PaginatedItems from '@/components/PaginatedItems';
import Error from '@/components/Error';
import CurrencyForm from '@/extensions/shop/views/Currency/Form';

export default {
    name: 'CurrenciesList',
    components: {
        PaginatedItems,
        Error,
        CurrencyForm,
    },
    data() {
        return {
            error: false,
            disable: {
                showFilters: false,
            },
            modal: {
                visibility: false,
                saving: false,
                data: {},
            },
            deleteDialog: {
                visibility: false,
                loading: false,
                target: null,
            },
        };
    },
    computed: {
        setupStore() {
            return useSetupStore(pinia);
        },
        currencyStore() {
            return useShopCurrencyStore(pinia);
        },
        activeCms() {
            return this.setupStore.activeCms;
        },
        activeShop() {
            return this.activeCms ? this.activeCms.shop : null;
        },
        loading() {
            return this.currencyStore.loading;
        },
        currenciesPaginated() {
            return this.currencyStore.currenciesPaginated;
        },
        filters() {
            const filters = _.cloneDeep(this.currenciesPaginated.filters || {});
            delete filters.cms;
            return filters;
        },
        showFilters() {
            return this.hasFilters || _.get(this.currenciesPaginated, 'showFilters') || false;
        },
        hasFilters() {
            return !_.isEmpty(this.filters);
        },
        sort() {
            const variables = this.currenciesPaginated.variables;
            const defaultSort = this.currenciesPaginated.defaultSort || { field: 'iso4217', direction: 'asc' };
            const order = (variables.order !== undefined && variables.order !== '') ? variables.order : defaultSort.field;
            const direction = (variables.direction !== undefined && variables.direction !== '') ? variables.direction : defaultSort.direction;
            return { field: order, direction };
        },
        existingIsoCodes() {
            return _.map(this.currenciesPaginated.items, 'iso4217');
        },
    },
    watch: {
        activeCms(cms) {
            const reload = _.get(cms, 'reloadComponents', true);
            if (reload) this.initialize();
        },
    },
    mounted() {
        this.initialize();
    },
    methods: {
        initialize() {
            this.updateLoading(true);
            this.refetch();
        },
        refetch() {
            this.updateLoading(true);
            const cmsId = _.get(this.activeCms, 'id');
            if (cmsId) {
                this.currencyStore.defineFilterValue({
                    key: 'cms',
                    value: { key: 'cms', match: cmsId, like: '', from: '', to: '', in: [] },
                });
            }
            return this.currencyStore.fetchCurrenciesPaginated()
                .then(() => this.updateLoading(false))
                .catch((err) => {
                    this.handleFetchError(err);
                    this.updateLoading(false);
                });
        },
        debouncedRefetch: _.debounce(function () {
            this.refetch();
        }, 300),
        handleFetchError(err) {
            const graphQLErrors = _.get(err, 'graphQLErrors');
            const code = _.get(graphQLErrors, '0.code') || 501;
            const message = _.get(graphQLErrors, '0.message') || 'An error occurred!';

            if (code === 401) {
                this.error = { code, message: 'You are not authorized' };
                return;
            }
            if (code === 403) {
                this.error = { code, message: 'You are not allowed to be here' };
                return;
            }

            this.error = true;
            this.$notify.error({
                title: this.$t(message),
                message: this.$t('Currency list could not be fetched'),
                position: 'bottom-left',
                customClass: 'dark',
                duration: 8000,
            });
            console.error(err);
        },
        updateLoading(value) {
            this.currencyStore.updateLoading(value);
        },
        handleSizeChange(perPage) {
            this.currencyStore.defineItemsPerPage(perPage);
            this.refetch();
        },
        handleCurrentChange(currentPage) {
            this.currencyStore.defineCurrentPage(currentPage);
            this.refetch();
        },
        handleColsShow(visibleColumns) {
            this.currencyStore.defineActiveColumns(visibleColumns);
            apolloClient.clearStore();
        },
        handleFilterInputChange(key, value, debounce = true) {
            this.currencyStore.defineCurrentPage(1);
            this.currencyStore.defineFilterValue({ key, value });
            if (debounce) this.debouncedRefetch();
            else this.refetch();
        },
        handleSortInputChange(key, value) {
            this.currencyStore.defineSorting({ key, value });
            this.refetch();
        },
        toggleFilterVisibility() {
            const value = !this.showFilters;
            this.disable.showFilters = true;
            this.currencyStore.defineFilterVisibility(value);
            this.setupStore.saveFilterVisibility({ scope: 'ex', key: 'shop_currency', value })
                .finally(() => { this.disable.showFilters = false; });
        },
        openModal(currency = null) {
            this.modal.data = currency
                ? { id: currency.id, iso4217: currency.iso4217, sign: currency.sign }
                : {};
            this.modal.visibility = true;
        },
        resetModal() {
            this.modal.visibility = false;
            this.modal.saving = false;
            this.modal.data = {};
        },
        async saveCurrency(payload) {
            this.modal.saving = true;
            try {
                await this.currencyStore.save(payload, this.activeShop ? this.activeShop.id : null);
                this.$notify.success({
                    title: this.$t('The currency has been saved successfully'),
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 6000,
                });
                this.modal.visibility = false;
                this.refetch();
            } catch (err) {
                console.error(err);
                this.$notify.error({
                    title: this.$t('The currency could not be saved'),
                    message: _.get(err, 'graphQLErrors.0.message') || '',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                });
            } finally {
                this.modal.saving = false;
            }
        },
        openDeleteDialog(currency) {
            this.deleteDialog.target = currency;
            this.deleteDialog.visibility = true;
        },
        closeDeleteDialog() {
            this.deleteDialog.visibility = false;
            this.deleteDialog.loading = false;
            this.deleteDialog.target = null;
        },
        async confirmDelete() {
            const target = this.deleteDialog.target;
            if (!target) return;
            this.deleteDialog.loading = true;
            try {
                await this.currencyStore.delete(target.id);
                this.$notify.success({
                    title: this.$t('The currency has been deleted successfully'),
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 6000,
                });
                this.modal.visibility = false;
                this.closeDeleteDialog();
                this.refetch();
            } catch (err) {
                console.error(err);
                this.$notify.error({
                    title: this.$t('The currency could not be deleted'),
                    message: _.get(err, 'graphQLErrors.0.message') || '',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                });
                this.deleteDialog.loading = false;
            }
        },
    },
};
</script>

<style lang="sass" scoped>
.currencies
    .table-top
        display: block
        .row
            display: flex
            justify-content: space-between
.modal-footer
    display: flex
    justify-content: flex-end
</style>
